import ClearIcon from "@mui/icons-material/Clear";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { GridColDef } from "@mui/x-data-grid";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useActiveFBOs } from "../../containers/ActiveFBOContainer";
import { useHangarsState } from "../../containers/HangarsStateContainer";
import { useLocationsState } from "../../containers/LocationsStateContainer";
import { useRampsState } from "../../containers/RampsStateContainer";
import { usePermissions } from "../../hooks/usePermissions";
import { Tenant } from "../../types";
import { Permission } from "../../utils/permissions";
import { lastHangaredText } from "../../utils/phrasing";
import { ConfirmationDialog } from "../../widgets/ConfirmationDialog";
import { EditTenant } from "../../widgets/EditTenant";
import { AddTenantButton } from "../Settings/HangarSettings/AddTenantButton";
import { TenantGrid } from "./TenantGrid";

import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ButtonNotes } from "../../widgets/ButtonNotes/ButtonNotes";

type Props = {
  filter: string;
  tenants: Tenant[];
  setTenant: (tenant: Tenant) => void;
  deleteTenant: (tenant: Tenant) => void;
  setFilter: (text: string) => void;
  editTenantId: string;
  setEditTenantId: (x: string) => void;
};

export const TenantsPresenter: React.FC<Props> = ({
  filter = "",
  tenants,
  setTenant,
  setFilter,
  deleteTenant,
  editTenantId,
  setEditTenantId,
}) => {
  const { t } = useTranslation();
  const { activeFBO } = useActiveFBOs();
  const { hasPermission } = usePermissions();
  const { ramps } = useRampsState();
  const { hangars } = useHangarsState();
  const { sendToLocation, locationOverides, findTenantLocation } =
    useLocationsState();
  const [tenantToDelete, setTenantToDelete] = React.useState<Tenant>();
  const [limitToAssignedLocationId, setLimitToAssignedLocationId] =
    React.useState<string>("");
  const [limitToCurrentLocationId, setLimitToCurrentLocationId] =
    React.useState<string>("");

  const viewOnly = !hasPermission(Permission.MODIFY_TENANTS);

  const tenantsToDisplay = React.useMemo(() => {
    return tenants.filter((t) => {
      if (
        !Boolean(filter) &&
        !Boolean(limitToAssignedLocationId) &&
        !Boolean(limitToCurrentLocationId)
      ) {
        return true;
      }

      const currentLocation = findTenantLocation(false, t);
      const referenceLocation = findTenantLocation(true, t);
      const currentLocationName = currentLocation?.name ?? "";
      const referenceLocationName = referenceLocation?.name ?? "";

      const queryString =
        `${t.type} - ${t.tail_number} - ${t.owner_manager} - ${t.aircraft.make} - ${t.aircraft.model} - ${t.note} - ${currentLocationName} - ${referenceLocationName}`.toLocaleLowerCase();

      const matchesQuerystring = Boolean(filter)
        ? queryString.indexOf(filter.toLocaleLowerCase()) > -1
        : true;

      if (limitToAssignedLocationId) {
        if (limitToAssignedLocationId === "ALL" && t.type === "base") {
          return matchesQuerystring;
        }
        if (
          limitToAssignedLocationId === "Unassigned" &&
          !referenceLocation &&
          t.type === "base"
        ) {
          return matchesQuerystring;
        }
        if (referenceLocation?.location_id === limitToAssignedLocationId) {
          return matchesQuerystring;
        }
        return false;
      }

      if (limitToCurrentLocationId) {
        if (limitToCurrentLocationId === "ALL") {
          return matchesQuerystring;
        }
        if (currentLocation?.location_id === limitToCurrentLocationId) {
          return matchesQuerystring;
        }
        return false;
      }

      return matchesQuerystring;
    });
  }, [
    tenants,
    filter,
    limitToAssignedLocationId,
    limitToCurrentLocationId,
    locationOverides,
  ]);

  const additionalColumns: GridColDef[] = [
    {
      field: "last_hangared",
      headerName: t("screens.tenants.columns.lastHangared"),
      width: 175,
      editable: false,
      renderCell: (params) => {
        return lastHangaredText(
          params.value,
          Boolean(findTenantLocation(false, params.row))
        );
      },
    },
    {
      field: "note",
      headerName: t("screens.tenants.columns.note"),
      width: 150,
      editable: false,
      renderCell: (params) => {
        return (
          <Tooltip title={params.row.note}>
            <Typography variant="inherit" noWrap>
              {params.row.note}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "action",
      headerName: "",
      sortable: false,
      width: 250,
      renderCell: (params) => {
        return (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            {(() => {
              const tenant = tenants.find(
                (t) => t.entity_id === params.row.entity_id
              );
              return tenant ? (
                <ButtonNotes
                  disabled={viewOnly}
                  variant="contained"
                  tenant={tenant}
                  setTenant={(updatedTenant: Tenant) => {
                    setTenant(updatedTenant);
                  }}
                />
              ) : null;
            })()}
            <Button
              disabled={viewOnly}
              size="small"
              variant="contained"
              color="inherit"
              onClick={() => setEditTenantId(params.row.entity_id)}
            >
              {t("screens.tenants.actions.edit")}
            </Button>
            <IconButton
              disabled={viewOnly}
              data-testid="delete"
              onClick={() => setTenantToDelete(params.row)}
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        );
      },
    },
  ];

  const selectedTenant = tenants.find(
    (tenant) => tenant.entity_id === editTenantId
  );

  return (
    <Box sx={{ p: 1, ml: 2, mr: 2, height: "100%" }}>
      <Stack direction="column" spacing={2} sx={{ height: "100%" }}>
        <Stack direction="row" justifyContent="space-between" sx={{ mt: 2 }}>
          <Typography variant="h6">
            {t("screens.tenants.title", { fboName: activeFBO?.name })}
          </Typography>
          <Stack direction="row" spacing={1}>
            <AddTenantButton
              location={null}
              sendToLocation={sendToLocation}
              onAdd={(tenant: Tenant | Tenant[]) => {
                if (Array.isArray(tenant)) {
                  // Bulk add: add all tenants individually
                  tenant.forEach((t) => setTenant(t));
                } else {
                  // Single add
                  setTenant(tenant);
                }
              }}
              stack={null}
              setStack={() => null}
              hideAddMovableObstacle={true}
              bulk={true}
            />
          </Stack>
        </Stack>
        <Stack direction="row" spacing={1}>
          <TextField
            fullWidth
            placeholder={t("screens.tenants.search.placeholder")}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            onChange={(evt) => setFilter(evt.target.value)}
            inputProps={{ testid: "search" }}
            value={filter}
          />

          <Stack direction="row" spacing={1} justifyContent="flex-start">
            <Stack direction="row" alignItems="center">
              <TextField
                select
                fullWidth
                label={t("screens.tenants.filters.showTenants.label")}
                value={limitToAssignedLocationId}
                onChange={(evt) => {
                  setLimitToCurrentLocationId("");
                  setLimitToAssignedLocationId(evt.target.value);
                }}
                sx={{ minWidth: 240 }}
              >
                <MenuItem value="ALL">{t("screens.tenants.filters.showTenants.all")}</MenuItem>
                <MenuItem value="Unassigned">{t("screens.tenants.filters.showTenants.unassigned")}</MenuItem>
                {[...hangars, ...ramps].map((location) => (
                  <MenuItem
                    key={`location-tenant-menu-${location.location_id}`}
                    value={location.location_id}
                  >
                    {t("screens.tenants.filters.showTenants.assignedTo", { locationName: location.name })}
                  </MenuItem>
                ))}
              </TextField>
              <IconButton onClick={() => setLimitToAssignedLocationId("")}>
                <ClearIcon />
              </IconButton>
            </Stack>
            <Stack direction="row" alignItems="center">
              <TextField
                select
                fullWidth
                label={t("screens.tenants.filters.currentLocation.label")}
                value={limitToCurrentLocationId}
                onChange={(evt) => {
                  setLimitToAssignedLocationId("");
                  setLimitToCurrentLocationId(evt.target.value);
                }}
                sx={{ minWidth: 240 }}
              >
                {[...hangars, ...ramps].map((location) => (
                  <MenuItem
                    key={`location-transient-menu-${location.location_id}`}
                    value={location.location_id}
                  >
                    {location.name}
                  </MenuItem>
                ))}
              </TextField>
              <IconButton onClick={() => setLimitToCurrentLocationId("")}>
                <ClearIcon />
              </IconButton>
            </Stack>
          </Stack>
        </Stack>

        <TenantGrid
          tenants={tenantsToDisplay}
          additionalColumns={additionalColumns}
        />
      </Stack>
      {Boolean(editTenantId) && (
        <EditTenant
          displayTypeAndLocation
          stack={null}
          setStack={null}
          tenant={selectedTenant}
          setTenant={setTenant}
          onClose={() => setEditTenantId(null)}
        />
      )}
      {Boolean(tenantToDelete) && (
        <ConfirmationDialog
          title={t("screens.tenants.deleteConfirmation.title", {
            identifier: tenantToDelete.tail_number || tenantToDelete.aircraft.model
          })}
          text={t("screens.tenants.deleteConfirmation.message")}
          okText={t("screens.tenants.deleteConfirmation.confirm")}
          onCancel={() => {
            setTenantToDelete(null);
          }}
          onOk={() => {
            deleteTenant(tenantToDelete);
            setTenantToDelete(null);
          }}
          onClose={() => {
            setTenantToDelete(null);
          }}
        />
      )}
    </Box>
  );
};
